import { Children, Fragment, cloneElement, ReactElement } from "react";
import { Search, X, Grid, Tag, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import FilterField from "@/components/ui/FilterField";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { useLocationOptions, findOptionMatch, optionLabel, withSelectedOption } from "@/lib/locations";
import { sanitizeLocationValue } from "@/lib/locationSanitizers";
import type { LocationValue } from "@/lib/locations";

interface CategoryLike {
  name: string;
  slug: string;
}

interface SubcategoryLike {
  name: string;
  slug: string;
}

interface Props {
  q: string;
  onQChange: (v: string) => void;
  onSubmit?: (payload: { q: string; category: string; location: LocationValue }) => void;
  placeholder?: string;
  qTestId?: string;
  category: string;
  categories: CategoryLike[];
  onCategoryChange: (v: string) => void;
  subcategory: string;
  subcategories: SubcategoryLike[];
  onSubcategoryChange: (v: string) => void;
  location: LocationValue;
  onLocationChange: (next: LocationValue) => void;
  locationContext?: { category?: string; q?: string };
  showCategory?: boolean;
  showSubcategory?: boolean;
  showLocation?: boolean;
  variant?: "hero" | "inline" | "listing";
  extras?: ReactElement | null;
  attributeFilters?: ReactElement[] | ReactElement | null;
  actionButton?: ReactElement | null;
  className?: string;
  testId?: string;
  hideSearchInput?: boolean;
  coreExtras?: ReactElement | null;
  onlineOnly?: boolean;
  onOnlineToggle?: () => void;
  sortMode?: string;
  onSortChange?: (v: string) => void;
}

export default function SearchBar({
  q,
  onQChange,
  onSubmit,
  placeholder = "Nome, palavra-chave…",
  qTestId = "search-q",
  category,
  categories = [],
  onCategoryChange,
  subcategory,
  subcategories = [],
  onSubcategoryChange,
  location,
  onLocationChange,
  locationContext,
  showCategory = true,
  showSubcategory = true,
  showLocation = true,
  variant = "hero",
  extras = null,
  attributeFilters = null,
  actionButton = null,
  className = "",
  testId,
  hideSearchInput = false,
  coreExtras = null,
  onlineOnly,
  onOnlineToggle,
  sortMode,
  onSortChange,
}: Props): React.JSX.Element {
  const wrapperClass = cn(
    "relative overflow-hidden rounded-[1.25rem] shadow-soft p-3 sm:p-4 flex flex-col gap-2 mt-4",
    "bg-white/85 backdrop-blur-md border border-white/20",
    "dark:bg-ink-800/85 dark:border-ink-700/30",
    className
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (typeof onSubmit === "function") onSubmit({ q, category, location });
  };

  const coreFilters: ReactElement[] = [];
  const advancedFilters: ReactElement[] = [];

  if (showCategory) {
    coreFilters.push(
      <div key="cat-separator" className="flex items-center gap-2 w-full pt-0 pb-1">
        <Grid className="w-3.5 h-3.5 text-heat-500" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-heat-700">Categorias</span>
        <span className="flex-1 h-px bg-cream-200" />
      </div>
    );
  }

  if (showCategory) {
    coreFilters.push(
      <FilterField key="cat" label="Categoria" variant={variant} icon={Grid}>
        <MultiSelect
          options={categories.map((c) => ({ label: c.name, value: c.slug }))}
          selected={category ? [category] : []}
          onChange={(vals) => onCategoryChange?.(vals[0] || "")}
          multiple={false}
          placeholder="Todas"
          className="!border-none !bg-transparent !p-0 !shadow-none !min-h-0 !h-auto"
          data-testid="search-category"
        />
      </FilterField>
    );
  }

  if (showSubcategory) {
    coreFilters.push(
      <FilterField key="sub" label="Subcategoria" variant={variant} icon={Tag}>
        <MultiSelect
          options={subcategories.map((s) => ({ label: s.name, value: s.slug }))}
          selected={subcategory ? [subcategory] : []}
          onChange={(vals) => onSubcategoryChange?.(vals[0] || "")}
          multiple={false}
          placeholder="Todas"
          className="!border-none !bg-transparent !p-0 !shadow-none !min-h-0 !h-auto"
          data-testid="search-subcategory"
        />
      </FilterField>
    );
  }

  const cleanLoc: LocationValue = {
    state: sanitizeLocationValue(location?.state),
    city: sanitizeLocationValue(location?.city),
    neighborhood: sanitizeLocationValue(location?.neighborhood),
  };
  const locOptions = useLocationOptions(cleanLoc, locationContext || {});

  if (showLocation && onLocationChange) {
    const updateLoc = (field: string, val: string) => {
      const next = { ...cleanLoc, [field]: val };
      if (field === "state") { next.city = ""; next.neighborhood = ""; }
      if (field === "city") next.neighborhood = "";
      onLocationChange(next);
    };

    const sOpts = withSelectedOption(locOptions.states, cleanLoc.state || "");
    const cOpts = withSelectedOption(locOptions.cities, cleanLoc.city || "");
    const nOpts = withSelectedOption(locOptions.neighborhoods, cleanLoc.neighborhood || "");

    const resS = findOptionMatch(sOpts, cleanLoc.state || "")?.value || cleanLoc.state || "";
    const resC = findOptionMatch(cOpts, cleanLoc.city || "")?.value || cleanLoc.city || "";
    const resN = findOptionMatch(nOpts, cleanLoc.neighborhood || "")?.value || cleanLoc.neighborhood || "";

    const selectStyles = "!border-none !bg-transparent !p-0 !shadow-none !min-h-0 !h-auto";

    coreFilters.push(
      <div key="loc-separator" className="flex items-center gap-2 w-full pt-3 pb-1">
        <MapPin className="w-3.5 h-3.5 text-heat-500" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-heat-700">Localização</span>
        <span className="flex-1 h-px bg-cream-200" />
      </div>
    );
    coreFilters.push(
      <FilterField key="state" label="Estado" variant={variant}>
        <MultiSelect
          options={sOpts.map(o => ({ label: optionLabel(o), value: o.value }))}
          selected={resS ? [resS] : []}
          onChange={(vals) => updateLoc("state", vals[0] || "")}
          multiple={false}
          placeholder="Todos"
          className={selectStyles}
        />
      </FilterField>
    );
    coreFilters.push(
      <FilterField key="city" label="Cidade" variant={variant}>
        <MultiSelect
          options={cOpts.map(o => ({ label: optionLabel(o), value: o.value }))}
          selected={resC ? [resC] : []}
          onChange={(vals) => updateLoc("city", vals[0] || "")}
          multiple={false}
          placeholder="Todas"
          className={selectStyles}
        />
      </FilterField>
    );
    coreFilters.push(
      <FilterField key="neighborhood" label="Bairro" variant={variant}>
        <MultiSelect
          options={nOpts.map(o => ({ label: optionLabel(o), value: o.value }))}
          selected={resN ? [resN] : []}
          onChange={(vals) => updateLoc("neighborhood", vals[0] || "")}
          multiple={false}
          placeholder="Todos"
          className={selectStyles}
        />
      </FilterField>
    );
  }

  if (coreExtras) {
    const items = coreExtras.type === Fragment
      ? Children.toArray((coreExtras as ReactElement & {props: any}).props.children as ReactElement[])
      : [coreExtras as ReactElement];
    items.forEach((item) => {
      if (item) coreFilters.push(item as ReactElement);
    });
  }

  if (Array.isArray(attributeFilters) && attributeFilters.length > 0 || extras) {
    advancedFilters.push(
      <div key="filter-separator" className="flex justify-center w-full">
        <span className="text-center overline mb-3 block">Pesquisa</span>
      </div>
    );
    if (attributeFilters) {
      if (Array.isArray(attributeFilters)) advancedFilters.push(...attributeFilters);
      else advancedFilters.push(attributeFilters as ReactElement);
    }
    if (extras) {
      const items = extras.type === Fragment
        ? Children.toArray((extras as ReactElement & {props: any}).props.children as ReactElement[])
        : [extras as ReactElement];
      items.forEach((item) => {
        if (item) advancedFilters.push(item as ReactElement);
      });
    }
  }

  const flatCore: ReactElement[] = [];
  const flatAdv: ReactElement[] = [];

  const doFlatten = (items: ReactElement[], targetArr: ReactElement[], prefix: string) => {
    Children.toArray(items).forEach((item, idx) => {
      if (!item) return;
      const el = item as ReactElement;
      if (el.type === Fragment) {
        doFlatten((el.props as ReactElement[]) as ReactElement[], targetArr, `${prefix}${idx}-`);
      } else if (Array.isArray(el)) {
        doFlatten(el, targetArr, `${prefix}${idx}-`);
      } else {
        const originalKey = el.key ? String(el.key) : `flat-${prefix}${idx}`;
        targetArr.push(cloneElement(el, { key: originalKey }));
      }
    });
  };

  doFlatten(coreFilters, flatCore, "c");
  doFlatten(advancedFilters, flatAdv, "a");

  const hasMore = flatAdv.length > 0;

  return (
    <form
      onSubmit={handleSubmit}
      className={wrapperClass}
      data-testid={testId}
    >
      {!hideSearchInput && (
        <div className="flex flex-col sm:flex-row items-stretch gap-2.5 w-full max-w-4xl mx-auto">
          <div className="flex-1 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border border-cream-200 bg-cream-50 min-w-0">
            <Search className="w-4 h-4 text-heat-500 shrink-0" aria-hidden="true" />
            <input
              type="text"
              aria-label="Pesificar por nome ou palavra-chave"
              value={q || ""}
              onChange={(e) => onQChange?.(e.target.value)}
              placeholder={placeholder}
              className="w-full bg-transparent outline-none text-sm text-ink-900 placeholder:text-ink-400"
              data-testid={qTestId}
            />
            {q && (
              <button
                type="button"
                onClick={() => onQChange?.("")}
                className="text-ink-400 hover:text-ink-700 shrink-0"
                aria-label="Limpar pesquisa"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      <div className={cn("flex flex-col gap-3", !hideSearchInput && "mt-1")}>
        {(!hideSearchInput || flatCore.length > 0 || flatAdv.length > 0 || actionButton) && (
          <div key="filter-separator" className="flex justify-center w-full">
            <span className="text-center overline mb-3 block">Pesquisa</span>
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch gap-2 w-full">
          {flatCore}
          {flatAdv}
        </div>

        {(hasMore || actionButton || onOnlineToggle || onSortChange) && (
          <div id="FILTROS_EXTRAS" className="shrink-0 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 w-full mt-3 pt-3 border-t border-cream-200/60">
            {(onOnlineToggle || onSortChange) && (
              <div className="flex flex-wrap items-center gap-2 mr-auto w-full sm:w-auto">
                {onOnlineToggle && (
                  <button
                    type="button"
                    onClick={onOnlineToggle}
                    className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border transition-all text-sm font-bold whitespace-nowrap shadow-sm ${
                      onlineOnly
                        ? "bg-emerald-800 border-emerald-900 text-white"
                        : "bg-emerald-700 border-emerald-800 text-white hover:bg-emerald-800"
                    }`}
                    data-testid="online-toggle"
                  >
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                    </span>
                    Online agora
                  </button>
                )}
                {onSortChange && (
                  <FilterField label="Ordenar por" className="sm:min-w-[160px]">
                    <MultiSelect
                      options={[
                        { label: "Destaques", value: "default" },
                        { label: "Recentes", value: "recent" },
                        { label: "Nome A-Z", value: "name" },
                        { label: "Próximos", value: "distance" },
                      ]}
                      selected={sortMode ? [sortMode] : ["default"]}
                      onChange={(vals) => onSortChange(vals[0] || "default")}
                      multiple={false}
                      className="!border-none !bg-transparent !p-0 !shadow-none !min-h-0 !h-auto"
                    />
                  </FilterField>
                )}
              </div>
            )}
            {actionButton && (
              <div className="flex items-stretch">
                {actionButton}
              </div>
            )}
          </div>
        )}
      </div>
    </form>
  );
}